<template>
  <div>
    <v-row no-gutters align="center" class="mb-3" style="gap: 12px">
      <v-col cols="6" sm="3" md="2">
        <dropdown
          v-model="filterPeriod"
          :items="periodOptions"
          item-text="label"
          item-value="value"
          :label="$t('finance_date')"
          hide-details
        />
      </v-col>
      <v-col cols="6" sm="3" md="2">
        <dropdown
          v-model="filterBranch"
          :items="branches"
          :label="$t('finance_branch')"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="6" sm="3" md="2">
        <dropdown
          v-model="filterType"
          :items="typeOptions"
          item-text="label"
          item-value="value"
          :label="$t('finance_type_label')"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="6" sm="3" md="2">
        <dropdown
          v-model="filterActor"
          :items="actors"
          :label="$t('finance_user')"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="6" sm="3" md="2">
        <text-field
          v-model.number="filterMinAmount"
          type="number"
          kind="money"
          min="0"
          :label="$t('finance_min_amount')"
          hide-details
        />
      </v-col>
      <v-col cols="auto">
        <v-btn text small @click="resetFilters">{{ $t("finance_reset_filters") }}</v-btn>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-card-title class="text-subtitle-1">{{ $t("finance_ledger_title") }}</v-card-title>
      <v-data-table
        :headers="headers"
        :items="filteredTransactions"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_transactions_filtered')"
        item-key="id"
        class="striped-table"
      >
        <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
        <template v-slot:item.type="{ item }">
          <span class="d-flex align-center">
            <v-icon small class="mr-1">{{ typeMeta(item.type).icon }}</v-icon>
            {{ typeMeta(item.type).labelKey ? $t(typeMeta(item.type).labelKey) : item.type }}
          </span>
        </template>
        <template v-slot:item.debit="{ item }">
          <span style="color: #d9820f; font-weight: 600">
            {{ item.amount < 0 ? formatCurrency(Math.abs(item.amount)) : "—" }}
          </span>
        </template>
        <template v-slot:item.credit="{ item }">
          <span style="color: #00b37e; font-weight: 600">
            {{ item.amount > 0 ? formatCurrency(item.amount) : "—" }}
          </span>
        </template>
        <template v-slot:item.balance="{ item }">
          <span class="font-weight-bold">{{ formatCurrency(item.runningBalance) }}</span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import gpsApi from "@/plugins/gpsApi";
import hasSnackbar from "@/mixins/hasSnackbar";

const TRANSACTION_TYPE_META = {
  order: { labelKey: "finance_tx_type_order", icon: "mdi-receipt-text-outline" },
  commission: { labelKey: "finance_tx_type_commission", icon: "mdi-percent-outline" },
  refund: { labelKey: "finance_tx_type_refund", icon: "mdi-cash-refund" },
  marketing: { labelKey: "finance_tx_type_marketing", icon: "mdi-bullhorn-outline" },
  delivery: { labelKey: "finance_tx_type_delivery", icon: "mdi-moped-outline" },
  payout: { labelKey: "finance_tx_type_payout", icon: "mdi-cash-check" },
  correction: { labelKey: "finance_tx_type_correction", icon: "mdi-file-edit-outline" },
};

// Nema posebnog endpointa za poslovnice/korisnike - GPS API vraća ove
// vrednosti kao slobodan tekst na svakoj transakciji, pa se liste
// filtera izvode iz podataka koji su vec ucitani (vidi loadFilterOptions).
const FALLBACK_ACTOR_KEYS = [
  "finance_actor_system",
  "finance_actor_courier",
  "finance_actor_restaurant",
  "finance_actor_admin",
];

export default {
  name: "FinanceTabTransactions",

  mixins: [hasSnackbar],

  data: () => ({
    loading: true,
    transactions: [],
    filterPeriod: "all",
    filterBranch: null,
    filterType: null,
    filterMinAmount: 0,
    filterActor: null,
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    periodOptions() {
      return [
        { value: "all", label: this.$t("finance_all") },
        { value: "7d", label: this.$t("finance_last_7_days") },
        { value: "30d", label: this.$t("finance_last_30_days") },
      ];
    },

    typeOptions() {
      return Object.entries(TRANSACTION_TYPE_META).map(([value, meta]) => ({
        value,
        label: this.$t(meta.labelKey),
      }));
    },

    headers() {
      return [
        { text: this.$t("finance_date"), value: "date" },
        { text: this.$t("finance_type_label"), value: "type" },
        { text: this.$t("finance_description"), value: "description" },
        { text: this.$t("finance_debit"), value: "debit", align: "end" },
        { text: this.$t("finance_credit"), value: "credit", align: "end" },
        { text: this.$t("finance_balance"), value: "balance", align: "end" },
      ];
    },

    transactionsWithBalance() {
      let balance = 0;
      return [...this.transactions]
        .sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id)
        .map((tx) => {
          balance += tx.amount;
          return { ...tx, runningBalance: balance };
        });
    },

    branches() {
      return [...new Set(this.transactions.map((t) => t.branch).filter(Boolean))];
    },

    actors() {
      const fromData = [...new Set(this.transactions.map((t) => t.actor).filter(Boolean))];
      return fromData.length > 0 ? fromData : FALLBACK_ACTOR_KEYS.map((key) => this.$t(key));
    },

    filteredTransactions() {
      const now = Date.now();
      const withinPeriod = (date) => {
        if (this.filterPeriod === "all") return true;
        const days = this.filterPeriod === "7d" ? 7 : 30;
        return now - new Date(date).getTime() <= days * 24 * 60 * 60 * 1000;
      };

      return [...this.transactionsWithBalance]
        .reverse()
        .filter((tx) => withinPeriod(tx.date))
        .filter((tx) => !this.filterBranch || tx.branch === this.filterBranch)
        .filter((tx) => !this.filterType || tx.type === this.filterType)
        .filter((tx) => !this.filterActor || tx.actor === this.filterActor)
        .filter((tx) => Math.abs(tx.amount) >= (this.filterMinAmount || 0));
    },
  },

  created() {
    this.load();
  },

  methods: {
    typeMeta(type) {
      return TRANSACTION_TYPE_META[type] || { labelKey: "", icon: "mdi-help-circle-outline" };
    },

    formatCurrency(value) {
      return `${Number(value || 0).toFixed(2)} ${this.restaurantCurrency}`;
    },

    formatDate(isoDate) {
      return new Date(isoDate).toLocaleDateString("sr-RS", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    },

    resetFilters() {
      this.filterPeriod = "all";
      this.filterBranch = null;
      this.filterType = null;
      this.filterActor = null;
      this.filterMinAmount = 0;
    },

    async load() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await gpsApi.get(`/restaurants/${this.restaurantId}/transactions`);
        this.transactions = data?.data || [];
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_transactions_load_error"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
